/* eslint-disable @typescript-eslint/no-explicit-any */
import { FIND_ASSIGNABLE_FILE_PRODUCTS } from '@gripp/shared-logic';
import { AssignmentListGrid, BaseColumnCheckbox, Colors } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Platform, StyleSheet, Text } from 'react-native';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EditFileProductsListProps {
  workspaceId: string;
  fileId: string;
  maxHeight?: number;
  keyword?: string;
  assignments: string[];
  updateAssignments: (assignments: string[]) => void;
}

export const EditFileProductsList = ({
  workspaceId,
  fileId,
  maxHeight,
  keyword,
  assignments,
  updateAssignments,
}: EditFileProductsListProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<any>();

  const columnHelper = createColumnHelper<any>();
  const columns: ColumnDef<any, any>[] = useMemo(
    () => [
      columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: { style: styles.checkboxCell },
      }),
      columnHelper.accessor('name', {
        cell: (info) => (
          <Text style={[styles.cellText, styles.nameLabel]}>
            {info.getValue()}
          </Text>
        ),
        header: () => t('products.list.name'),
        meta: { style: styles.cell },
      }),
    ],
    [t]
  );

  useEffect(() => {
    setFilter({
      keyword: keyword || undefined,
      mediaId: fileId,
      assignments,
      workspaceId,
    });
  }, [workspaceId, fileId, keyword]);

  if (!filter) return null;

  return (
    <AssignmentListGrid
      modelName="product"
      query={FIND_ASSIGNABLE_FILE_PRODUCTS}
      dataPrefix="media.findAssignableProductsForFile"
      columns={columns}
      filter={filter}
      assignments={assignments}
      onAssignmentsChange={updateAssignments}
      maxHeight={maxHeight}
      textEmpty={t('products.textEmpty')}
      orderBy={{ field: 'name', order: 'ASC' as any }}
    />
  );
};

const styles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: { height: 50, paddingRight: 16 },
      default: { height: 68, paddingRight: 12 },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: { color: Colors.black },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: { fontWeight: '500', flexWrap: 'nowrap' },
      default: { fontWeight: '400', flexWrap: 'wrap' },
    }),
  },
});
